import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import ProductBrand from '@/components/feature/ProductBrand';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!email) {
      setError('Vui lòng nhập email!');
      return;
    }

    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}${__BASE_PATH__}/reset-password`;
      console.log('Sending reset email with redirectTo:', redirectTo);
      
      const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });

      if (err) {
        console.error('Supabase reset error:', err);
        setError(err.message);
      } else {
        setSuccess(true);
      }
    } catch {
      setError('Đã xảy ra lỗi. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f7f4] flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-amber-100/60 rounded-full"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-amber-50/80 rounded-full"></div>
      </div>

      <div className="relative w-full max-w-md">
        <ProductBrand />

        {/* Card */}
        <div className="bg-white rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <span className="w-6 h-6 flex items-center justify-center text-amber-500">
                <i className="ri-mail-send-line text-xl"></i>
              </span>
            </div>
            <h2 className="text-lg font-bold text-[#1a2332]">Khôi phục mật khẩu</h2>
            <p className="text-gray-400 text-xs mt-1">
              Nhập email đã đăng ký, chúng tôi sẽ gửi link đặt lại mật khẩu.
            </p>
          </div>

          {success ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
                <span className="w-4 h-4 flex items-center justify-center text-emerald-500 shrink-0">
                  <i className="ri-checkbox-circle-line text-sm"></i>
                </span>
                <p className="text-emerald-600 text-xs">
                  Đã gửi email khôi phục! Vui lòng kiểm tra hộp thư và làm theo hướng dẫn.
                </p>
              </div>
              <p className="text-gray-400 text-xs text-center">
                Nếu không thấy email, hãy kiểm tra thư mục Spam / Rác.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-[#f59e0b] text-white rounded-xl text-sm font-bold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap"
              >
                Quay lại đăng nhập
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Email</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-mail-line text-base"></i>
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                  <span className="w-4 h-4 flex items-center justify-center text-red-500 shrink-0">
                    <i className="ri-error-warning-line text-sm"></i>
                  </span>
                  <p className="text-red-600 text-xs">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#f59e0b] text-white rounded-xl text-sm font-bold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Đang gửi...
                  </span>
                ) : (
                  'Gửi link khôi phục'
                )}
              </button>

              {/* Back to login */}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full py-2.5 text-gray-500 text-sm font-semibold hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Quay lại đăng nhập
              </button>
            </form>
          )}
        </div>

        <ProductBrand footer />
      </div>
    </div>
  );
}